import { DatePicker, Button } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import "./RangePickerWithPresets.scss";

const { RangePicker } = DatePicker;

const normalizeRange = ([start, end]) => [
  start ? start.startOf("day") : null,
  end ? end.endOf("day") : null,
];

const rangePresets = [
  {
    label: "Current month",
    value: normalizeRange([dayjs().startOf("month"), dayjs()]),
    key: "CM",
  },
  {
    label: "Last 1 month",
    value: normalizeRange([dayjs().subtract(1, "month"), dayjs()]),
    key: "1M",
  },
  {
    label: "Last 3 month",
    value: normalizeRange([dayjs().subtract(3, "month"), dayjs()]),
    key: "3M",
  },
  {
    label: "Last 6 month",
    value: normalizeRange([dayjs().subtract(6, "month"), dayjs()]),
    key: "6M",
  },
  {
    label: "Last 1 year",
    value: normalizeRange([dayjs().subtract(1, "year"), dayjs()]),
    key: "1Y",
  },
  {
    label: "All time",
    value: normalizeRange([dayjs("2000-01-01"), dayjs()]),
    key: "ALL",
  },
];

const RangePickerWithPresets = ({ onDateChange, value = null, activeKey }) => {
  const [range, setRange] = useState([]);
  const [selectedKey, setSelectedKey] = useState(activeKey || null);

  useEffect(() => {
    if (Array.isArray(value) && value.length === 2) {
      const normalized = normalizeRange([value[0], value[1]]);
      setRange(normalized);
    } else if (Array.isArray(value)) {
      /* A controlled parent that has cleared its range hands back an empty
         array. Without this the field kept showing the dates it had just
         been told to drop, and "Clear filters" looked like it did nothing. */
      setRange([]);
    } else if (!value) {
      const defaultPreset = rangePresets.find((p) => p.key === "CM");
      setRange(defaultPreset.value);
      setSelectedKey(defaultPreset.key);
      onDateChange?.(
        defaultPreset.value[0],
        defaultPreset.value[1],
        defaultPreset.key
      );
    }
  }, [value]);

  const handleChange = (dates) => {
    if (dates) {
      const normalizedDates = normalizeRange([
        dates[0].clone(),
        dates[1].clone(),
      ]);
      setRange(normalizedDates);
      setSelectedKey(null);
      onDateChange?.(normalizedDates[0], normalizedDates[1], null);
    } else {
      setRange([]);
      setSelectedKey(null);
      onDateChange?.(null, null, null);
    }
  };

  const handlePresetClick = (preset) => {
    setRange(preset.value);
    setSelectedKey(preset.key);
    onDateChange?.(preset.value[0], preset.value[1], preset.key);
  };

  return (
    <div className="_range_picker_">
      <RangePicker
        value={range}
        onChange={handleChange}
        format="DD MMM YYYY"
        renderExtraFooter={() => (
          <div className="preset-wrapper">
            {rangePresets.map((preset) => (
              <Button
                key={preset.key}
                type={selectedKey === preset.key ? "primary" : "default"}
                size="small"
                className={`preset-item ${
                  selectedKey === preset.key ? "active" : ""
                }`}
                onClick={() => handlePresetClick(preset)}
                style={{ margin: "2px" }}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        )}
        getPopupContainer={() =>
          document.querySelector(".ant-modal-content") || document.body
        }
        style={{ height: "35px" }}
        suffixIcon={false}
        onOpenChange={(open) => {
          /* length check added with the clear branch above: an empty
             controlled value is still truthy, and re-normalising it wrote
             [null, null] back over a range the user had just picked. */
          if (!open && Array.isArray(value) && value.length === 2) {
            const normalized = normalizeRange([value[0], value[1]]);
            setRange(normalized);
          }
        }}
      />
    </div>
  );
};

export default RangePickerWithPresets;
